import { memo } from 'react';
import AppLayout from '@/react-app/components/AppLayout';
import { useApi } from '@/react-app/hooks/useApi';
import { useQualificacoesHistorico } from '@/react-app/hooks/useQualificacoesExt';

type Funcionario = {
  id: number;
  nome: string;
  status?: string;
};

function DashboardNew() {
  const { data: funcionariosData } = useApi('/api/funcionarios');
  const { stats, loading: loadingQual } = useQualificacoesHistorico(undefined, 1, 1); // Só precisa de stats, não dos dados

  // Extrair funcionários do envelope de resposta
  const funcionarios: Funcionario[] = Array.isArray(funcionariosData)
    ? (funcionariosData as Funcionario[])
    : (funcionariosData as { data?: Funcionario[] })?.data || [];

  const totalFuncionarios = funcionarios.length;
  const funcionariosAtivos = funcionarios.filter((f: Funcionario) => f.status === 'ATIVO').length;

  return (
    <AppLayout>
      {/* Page Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
          Planejador de Conformidade Proativo
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Visão geral do sistema e indicadores de conformidade - Atualizado agora mesmo
        </p>
      </div>

      {/* KPI Cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
            Funcionários Ativos
          </p>
          <p className="text-2xl font-bold text-success">{funcionariosAtivos}</p>
          <p className="text-xs text-slate-400 mt-1">{totalFuncionarios} total</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
            Qualificações Válidas
          </p>
          <p className="text-2xl font-bold text-success">{loadingQual ? '...' : stats.validas}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
            Vencendo (30 dias)
          </p>
          <p className="text-2xl font-bold text-warning">{loadingQual ? '...' : stats.vencendo}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Vencidas</p>
          <p className="text-2xl font-bold text-critical">{loadingQual ? '...' : stats.vencidas}</p>
        </div>
      </div>

      {/* Ações Rápidas */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <a
          href="/funcionarios"
          className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-6 transition-all hover:border-primary-600 hover:shadow-md"
        >
          <span className="material-symbols-outlined text-4xl text-primary-600">person_add</span>
          <h3 className="text-lg font-semibold text-slate-900">Cadastrar Funcionário</h3>
          <p className="text-sm text-slate-600">Adicione novos colaboradores ao sistema</p>
        </a>

        <a
          href="/qualificacoes"
          className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-6 transition-all hover:border-primary-600 hover:shadow-md"
        >
          <span className="material-symbols-outlined text-4xl text-primary-600">badge</span>
          <h3 className="text-lg font-semibold text-slate-900">Gerenciar Qualificações</h3>
          <p className="text-sm text-slate-600">Visualize e atualize certificações</p>
        </a>

        <a
          href="/simuladores"
          className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-6 transition-all hover:border-primary-600 hover:shadow-md"
        >
          <span className="material-symbols-outlined text-4xl text-primary-600">
            calendar_today
          </span>
          <h3 className="text-lg font-semibold text-slate-900">Agendar Sessão</h3>
          <p className="text-sm text-slate-600">Programe treinamentos de simulador</p>
        </a>
      </div>

      {/* Alertas de Conformidade */}
      {stats.vencidas > 0 && (
        <div className="mb-8 rounded-lg border border-danger-200 bg-danger-50 p-6">
          <div className="flex items-start gap-4">
            <span className="material-symbols-outlined text-3xl text-danger-600">warning</span>
            <div>
              <h3 className="text-lg font-semibold text-danger-900 mb-1">
                Atenção: Qualificações Vencidas
              </h3>
              <p className="text-sm text-danger-700 mb-3">
                Existem {stats.vencidas} qualificações vencidas que requerem atenção imediata.
              </p>
              <a
                href="/qualificacoes"
                className="inline-flex items-center gap-2 rounded-md bg-danger-600 px-4 py-2 text-sm font-medium text-white hover:bg-danger-700"
              >
                Ver Detalhes
                <span className="material-symbols-outlined text-base">arrow_forward</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {stats.vencendo > 0 && stats.vencidas === 0 && (
        <div className="mb-8 rounded-lg border border-warning-200 bg-warning-50 p-6">
          <div className="flex items-start gap-4">
            <span className="material-symbols-outlined text-3xl text-warning-600">schedule</span>
            <div>
              <h3 className="text-lg font-semibold text-warning-900 mb-1">
                Qualificações Próximas ao Vencimento
              </h3>
              <p className="text-sm text-warning-700 mb-3">
                Existem {stats.vencendo} qualificações que vencerão em breve. Planeje as renovações.
              </p>
              <a
                href="/qualificacoes"
                className="inline-flex items-center gap-2 rounded-md bg-warning-600 px-4 py-2 text-sm font-medium text-white hover:bg-warning-700"
              >
                Ver Detalhes
                <span className="material-symbols-outlined text-base">arrow_forward</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

export default memo(DashboardNew);
